import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { Card } from 'react-bootstrap-5';
import { listItemPropTypes, reorderableListItem } from 'helpers/react_dnd/reorderable_list';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamation, faPenToSquare } from '@fortawesome/pro-solid-svg-icons';

export class PhotoRow extends React.Component {
  static propTypes = {
    ...listItemPropTypes,
    classSlug: PropTypes.string.isRequired,
    defaultPhotoOption: PropTypes.string.isRequired,
    isChecked: PropTypes.bool,
    photoAttachment: PropTypes.object.isRequired,
    photoEditorUrl: PropTypes.string,
    photoTypeOptions: PropTypes.array.isRequired,
    showEdit: PropTypes.bool,
    onAttributeChange: PropTypes.func.isRequired,
    onCheckboxClicked: PropTypes.func,
    onEdit: PropTypes.func.isRequired
  }

  static defaultPropTypes = {
    isChecked: false,
    showEdit: true
  }

  handleDescriptionUpdate = (e) => {
    this.props.onAttributeChange(this.props.index, 'description', e.target.value);
  }

  handleAttachmentTypeUpdate = (e) => {
    this.props.onAttributeChange(this.props.index, 'attachmentType', e.target.value);
  }

  handleSyndicateUpdate = (newValue) => {
    this.props.onAttributeChange(this.props.index, 'syndicate', newValue);
  }

  deletePhoto = (e) => {
    e.preventDefault();
    this.props.onAttributeChange(this.props.index, 'deleted', true);
  }

  getInputName = (key) => {
    let name = `${this.props.classSlug}[photo_attachments_attributes][${this.props.index}]`;
    const photoAttributeKeys = [
      'upload_id',
      '_destroy',
      'attachment_type',
      'id'
    ];

    if (photoAttributeKeys.indexOf(key) > -1) {
      name += `[photo_attributes]`;
    }
    name += `[${key}]`;
    return name;
  }

  render() {
    if (this.props.photoAttachment.deleted && this.props.photoAttachment.uploaded) {
      return null;
    }

    const { isChecked, photoAttachment, index } = this.props;
    const style = {
      opacity: this.props.isDragging ? 0 : 1,
      display: photoAttachment.deleted ? 'none' : 'block'
    };

    const photoRowBody = this.props.connectDragPreview(this.props.connectDropTarget(
      <div className="photo-row" key={index} name="photo-row-container" style={style}>
        <Card>
          <Card.Header className="position-relative p-0">
            {this.renderDragSource()}
            <div className="position-absolute top-0 start-0 p-2">
              <input
                checked={isChecked}
                className="form-check-input border-dark"
                style={{ width: '1.15rem', height: '1.15rem' }}
                type="checkbox"
                onChange={this.props.onCheckboxClicked}
              />
            </div>
            <div className="position-absolute d-flex align-items-center gap-3" style={{ right: '10px', top: '5.5px' }}>
              {this.renderStatusBadge()}
              {this.renderSyndicationInput()}
            </div>
          </Card.Header>
          {this.renderDescriptionInput(photoAttachment, index)}
          <Card.Body className="p-2">
            <div className="d-flex align-items-center gap-2 justify-content-between">
              {this.props.showEdit && this.renderEditButton()}
              <div>
                { this.renderAttachmentTypeInput() }
              </div>
            </div>
            <div className="d-none">
              {this.renderHiddenIdField()}
              {this.renderHiddenSortOrderField()}
              {this.renderHiddenDeleteField()}
            </div>
          </Card.Body>
        </Card>
      </div>
    ));

    return photoRowBody;
  }

  newSidekiqImage() {
    return !isEmpty(this.props.photoAttachment.sidekiqStatus);
  }

  bannerLabel() {
    if (this.newSidekiqImage()) {
      return this.props.photoAttachment.sidekiqStatus;
    } else {
      return 'Processing';
    }
  }

  photoPreviewStyle() {
    const { processing, sidekiqStatus: status } = this.props.photoAttachment;
    const url = this.props.photoAttachment.url;

    if (processing && (status === 'complete' || status === undefined)) {
      return { backgroundImage: `url(${url})` };
    } else if (processing) {
      return { backgroundImage: 'none' };
    } else {
      return { backgroundImage: `url(${url})` };
    }
  }

  renderTooltip() {
    const status = this.props.photoAttachment.sidekiqStatus;

    if (status === 'failed') {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          This media failed to process. This was mostly likely caused by an image that is too large.
          Try reducing the image size or contact Buildout support.
        </Tooltip>
      );
    } else if (status !== 'complete' && status != null) {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          Your media is being optimized for the best performance. This may take a few minutes.
        </Tooltip>
      );
    } else {
      return (
        <Tooltip id={`primaryPhotoTooltip${this.props.index}`}>
          Primary photos will be the first to be displayed in documents, email blasts and plugins.
          Choose a photo that best represents this {this.props.classSlug} as the primary photo.
        </Tooltip>
      );
    }
  }

  renderDragSource() {
    const labelStyle = {
      display: this.props.index === 0 ? 'block' : 'none'
    };

    return this.props.connectDragSource(
      <div className="js-photo-preview" style={this.photoPreviewStyle()} />
    );
  }

  renderStatusBadge() {
    const labelStyle = {
      display: this.props.index === 0 ? 'block' : 'none'
    };

    return (
      <div className="photo-badge-container">
        <OverlayTrigger
          overlay={this.renderTooltip()}
          placement="top"
        >
          {
            this.props.photoAttachment.processing ?
              <Badge className="photo-label-badge">{this.bannerLabel()}</Badge> :
              <Badge className="photo-label-badge" style={labelStyle}>Primary</Badge>
          }
        </OverlayTrigger>
      </div>
    );
  }

  renderHiddenIdField() {
    const { photoAttachment, index } = this.props;
    if (photoAttachment.uploaded) {
      return (
        <input
          name={this.getInputName('upload_id')}
          readOnly
          type="hidden"
          value={photoAttachment.uploadId}
        />
      );
    }
    let newUploadIdField = null;
    if (photoAttachment.uploadId) {
      newUploadIdField = (
        <input
          name={this.getInputName('upload_id')}
          readOnly
          type="hidden"
          value={photoAttachment.uploadId}
        />
      );
    }
    return (
      <>
        {newUploadIdField}
        <input
          name={`${this.props.classSlug}[photo_attachments_attributes][${index}][id]`}
          readOnly
          type="hidden"
          value={photoAttachment.id}
        />
        <input
          name={this.getInputName('photo_id')}
          readOnly
          type="hidden"
          value={photoAttachment.photoId}
        />
        <input
          name={this.getInputName('id')}
          readOnly
          type="hidden"
          value={photoAttachment.photoId}
        />
      </>
    );
  }

  renderHiddenSortOrderField() {
    return (
      <input
        name={this.getInputName('sort_order')}
        readOnly
        type="hidden"
        value={this.props.index}
      />
    );
  }

  renderHiddenDeleteField() {
    return (
      <input
        className="deletePhotoInput"
        name={this.getInputName('_destroy')}
        readOnly
        type="hidden"
        value={this.props.photoAttachment.deleted ? '1' : '0'}
      />
    );
  }

  renderDescriptionInput() {
    return (
      <input
        className="form-control border-0"
        name={this.getInputName('description')}
        placeholder="Add Caption"
        type="text"
        value={this.props.photoAttachment.description || ''}
        onChange={this.handleDescriptionUpdate}
      />
    );
  }

  displayedPhotoTypeOptions() {
    const { photoTypeOptions } = this.props;
    return photoTypeOptions.map(option => <option key={option} value={option}>{option}</option>);
  }

  renderAttachmentTypeInput() {
    const { defaultPhotoOption, photoAttachment, photoTypeOptions } = this.props;

    return (
      <select
        className="form-control form-select border-0 text-end"
        name={this.getInputName('attachment_type')}
        value={photoAttachment.attachmentType || defaultPhotoOption}
        onChange={this.handleAttachmentTypeUpdate}
      >
        {this.displayedPhotoTypeOptions()}
      </select>
    );
  }

  renderSyndicationInput() {
    const { syndicate } = this.props.photoAttachment;

    const tooltipText = `This photo is${syndicate ? '' : ' not'} approved for Syndication`;

    const tooltip = <Tooltip id={`propertySyndicationTooltip${this.props.index}`}>{tooltipText}</Tooltip>;

    return (
      <OverlayTrigger overlay={tooltip} placement="top">
        <button
          className="photo-syndication-button"
          onClick={() => this.handleSyndicateUpdate(!this.props.photoAttachment.syndicate)}
        >
          {this.props.photoAttachment.syndicate ? (
            <FontAwesomeIcon className="ds-text-secondary" icon={faCheck} size="lg"/>
          ) : (
            <FontAwesomeIcon className="ds-text-danger" icon={faExclamation} size="lg"/>
          )}
        </button>
      </OverlayTrigger>
    );
  }

  renderEditButton() {
    return (
      <Button
        variant="ghost"
        onClick={() => this.props.onEdit(this.props.photoAttachment)}
      >
        <FontAwesomeIcon icon={faPenToSquare} />
        Edit
      </Button>
    );
  }
}

export default reorderableListItem(PhotoRow, 'photoForm', {
  beginDrag(props) {
    return {
      id: props.photoAttachment.id,
      index: props.index
    };
  }
});
